import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Section, Overline } from "@/components/Primitives";
import { Phone, MessageCircle, Send, Check } from "lucide-react";

export default function Reservation() {
  const { t, i18n } = useTranslation();
  const { settings: s } = useSettings();
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    date: "", time: "", guests: 2, message: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: "" });
    try {
      await api.post("/reservations", { ...form, guests: Number(form.guests), language: i18n.language });
      setStatus({ loading: false, ok: true, error: "" });
      setForm({ first_name: "", last_name: "", phone: "", email: "", date: "", time: "", guests: 2, message: "" });
    } catch {
      setStatus({ loading: false, ok: false, error: t("reservation_page.error") });
    }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const waMsg = encodeURIComponent(`Bonjour Le 17, je souhaite réserver une table le ${form.date || "[date]"} à ${form.time || "[heure]"} pour ${form.guests} personnes. Merci.`);

  return (
    <div data-testid="reservation-page" className="pt-32">
      <Section testId="reservation-header">
        <Overline>{t("reservation_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-6">{t("reservation_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/70 leading-relaxed">{t("reservation_page.intro")}</p>
      </Section>

      <Section testId="reservation-form" className="!pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={submit} className="lg:col-span-7 luxe-card p-8 lg:p-10 space-y-5" data-testid="reservation-form-block">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="overline mb-2 block">{t("reservation_page.first_name")}</label>
                <input data-testid="res-first-name" required value={form.first_name} onChange={e => upd("first_name", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("reservation_page.last_name")}</label>
                <input data-testid="res-last-name" required value={form.last_name} onChange={e => upd("last_name", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("reservation_page.phone")}</label>
                <input data-testid="res-phone" required type="tel" value={form.phone} onChange={e => upd("phone", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("reservation_page.email")}</label>
                <input data-testid="res-email" required type="email" value={form.email} onChange={e => upd("email", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("reservation_page.date")}</label>
                <input data-testid="res-date" required type="date" value={form.date} onChange={e => upd("date", e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">{t("reservation_page.time")}</label>
                <input data-testid="res-time" required type="time" value={form.time} onChange={e => upd("time", e.target.value)} className="input-luxe" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">{t("reservation_page.guests")}</label>
                <input data-testid="res-guests" required type="number" min="1" max="30" value={form.guests} onChange={e => upd("guests", e.target.value)} className="input-luxe" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">{t("reservation_page.message")}</label>
                <textarea data-testid="res-message" rows={4} value={form.message} onChange={e => upd("message", e.target.value)} className="input-luxe" />
              </div>
            </div>

            <div className="pt-2">
              <button data-testid="res-submit" type="submit" disabled={status.loading} className="btn-gold w-full sm:w-auto disabled:opacity-50">
                {status.loading ? "..." : t("reservation_page.submit")} <Send size={14} />
              </button>
              {status.ok && (
                <div data-testid="res-success" className="mt-4 flex items-center gap-2 text-emerald-400 text-sm">
                  <Check size={16} /> {t("reservation_page.success")}
                </div>
              )}
              {status.error && (
                <div data-testid="res-error" className="mt-4 text-red-400 text-sm">{status.error}</div>
              )}
              <p className="mt-4 text-xs text-[#F8F5EF]/50 italic">{t("reservation_page.confirmation_note")}</p>
            </div>
          </form>

          <aside className="lg:col-span-5 space-y-4">
            <div className="overline">{t("reservation_page.or")}</div>
            {s && (
              <>
                <a data-testid="res-call-btn" href={`tel:${s.phone?.replace(/\s/g, "")}`} className="btn-ghost-gold w-full justify-between">
                  <span className="flex items-center gap-3"><Phone size={16} /> {t("reservation_page.call_btn")}</span>
                  <span className="text-[#D4AF37]">{s.phone}</span>
                </a>
                <a data-testid="res-whatsapp-btn" href={`https://wa.me/${s.whatsapp_raw}?text=${waMsg}`} target="_blank" rel="noreferrer" className="btn-ghost-gold w-full justify-between">
                  <span className="flex items-center gap-3"><MessageCircle size={16} /> {t("reservation_page.whatsapp_btn")}</span>
                  <span className="text-[#D4AF37]">{s.whatsapp}</span>
                </a>
              </>
            )}
            <div className="luxe-card p-6 mt-6">
              <div className="overline mb-2">{t("footer.hours_label")}</div>
              <div className="text-sm text-[#F8F5EF]/80 whitespace-pre-line">{s?.hours?.[i18n.language] || s?.hours?.fr}</div>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}
